import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { FC, useEffect, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { selectUser } from '../../redux/reducers/userSlice';
import { useAuth0 } from '@auth0/auth0-react';
import { useLearnosityBffInitialization } from '../../custom-hooks/useLearnosityBffInitialization';
import { exitSlipRegions } from '../../learnosity-regions/ExitSlipRegions';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import ExitSlipPrintModalWrapper from './ExitSlipPrintModalWrapper';
import { getItemReferences } from '../../bffApiCalls/getItemReferences';
import { v4 as uuid } from 'uuid';
import { PrintModalData } from './ExitSlipModalBodyData';
import { format } from 'date-fns';
import { Expand, Printer, Shrink } from 'lucide-react';
import { usePostHog } from '@posthog/react';
import { isAbortError } from '../../utils/fetchCancellation';

interface Props {
  setPrintModalData: (v: PrintModalData) => void;
  printModalData: PrintModalData;
}

const ExitSlipPrintModal: FC<Props> = ({ setPrintModalData, printModalData }) => {
  const user = useAppSelector(selectUser);
  const activityId = printModalData.activityId ?? '';
  const [request, setRequest] = useState<LearnosityRequest | null>(null);
  const [itemReferences, setItemReferences] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [maskStyle, setMaskStyle] = useState<number>(1);
  const [fullScreen, setFullScreen] = useState<boolean>(false);

  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();
  const { fetchInitialization, refetchInitialization } = useLearnosityBffInitialization(
    getAccessTokenSilently,
    logout,
    posthog,
  );

  useEffect(() => {
    const controller = new AbortController();
    setRequest(null);
    setItemReferences(null);
    setLoadError(false);
    setLoading(true);
    if (printModalData.visible && user && user.sub) {
      getItemReferences(activityId, getAccessTokenSilently, logout, posthog, { signal: controller.signal })
        .then(itemRef => {
          if (controller.signal.aborted) return;
          if (!itemRef.length) {
            setLoading(false);
            setLoadError(true);
            return;
          }
          setItemReferences(itemRef);
        })
        .catch(err => {
          if (controller.signal.aborted || isAbortError(err)) return;
          console.log('Error fetching item references', err);
          setLoading(false);
          setLoadError(true);
        });

      fetchInitialization(
        {
          userId: user?.sub,
          sessionId: uuid(),
          state: 'initial',
          activityId: activityId,
          title: 'Exit Slip View',
          regions: exitSlipRegions,
          renderingType: 'inline',
          showDistractorRationale: true,
          responsiveRegions: true,
          warningOnChange: false,
        },
        { signal: controller.signal },
      )
        .then(data => {
          if (controller.signal.aborted) return;
          setRequest(data);
          setMaskStyle(6);
        })
        .catch(error => {
          if (controller.signal.aborted || isAbortError(error)) return;
          console.log('Error fetching Learnosity bridge');
          setLoading(false);
          setLoadError(true);
        });
    }
    return () => controller.abort();
  }, [activityId, user?.sub, printModalData.visible]);

  const headerTemplate = (
    <div className="tw-flex tw-flex-col">
      <h4>Exit ticket for {printModalData.title}</h4>
      {printModalData.date && <p>This lesson is scheduled for {format(printModalData.date, 'MM-dd-yyyy')}</p>}
    </div>
  );

  const footerTemplate = (
    <div className="tw-flex tw-self-end tw-w-full tw-justify-end ">
      {printModalData.fullScreenButton ? (
        <Button
          label={fullScreen ? 'Exit full screen' : 'Full screen'}
          tooltip={!fullScreen ? 'Display in fullscreen for projector' : ''}
          tooltipOptions={{ position: 'top' }}
          outlined
          disabled={loading || loadError}
          className="tw-gap-1 mr-2"
          icon={fullScreen ? <Shrink size={14} /> : <Expand size={14} />}
          onClick={() => setFullScreen(prev => !prev)}
        />
      ) : (
        <Button
          label="Cancel"
          outlined
          onClick={() => setPrintModalData({ ...printModalData, visible: false })}
          className="mr-2"
        />
      )}
      {!fullScreen && (
        <Button
          label="Print"
          disabled={loading || loadError}
          onClick={() => {
            //temporary adding a css class to hide the root from behing the modal
            document.body.classList.add('printing-modal');
            setTimeout(() => {
              window.print();
              document.body.classList.remove('printing-modal');
            }, 0);
          }}
          icon={<Printer size={14} />}
          className="tw-gap-1 mr-2"
        />
      )}
    </div>
  );

  return (
    <Dialog
      header={headerTemplate}
      visible={printModalData.visible}
      style={{
        width: fullScreen ? '100%' : '70vw',
        height: fullScreen ? '100%' : '70vh',
        maxHeight: fullScreen ? '100%' : 'inherit',
        maxWidth: fullScreen ? '100%' : 'inherit',
        zIndex: 5,
      }}
      onHide={() => setPrintModalData({ ...printModalData, visible: false })}
      footer={footerTemplate}
      maskStyle={{ zIndex: maskStyle }}
      className={`nc_exit-slip-print-modal ${fullScreen ? 'full-screen' : ''}`}
    >
      <>
        {request && itemReferences?.length && (
          <ExitSlipPrintModalWrapper
            request={request}
            enabled={printModalData.visible}
            setLoading={setLoading}
            itemRefs={itemReferences}
            refetchInitialization={refetchInitialization}
            onFailure={() => {
              setLoading(false);
              setLoadError(true);
            }}
          />
        )}
        {loading && !loadError && <ThreeDotLoader dark />}
        {loadError && (
          <p role="alert" className="tw-text-center tw-text-base tw-text-neutral-700 tw-py-10">
            We couldn&apos;t load this exit ticket. Please close this window and try again.
          </p>
        )}
      </>
    </Dialog>
  );
};

export default ExitSlipPrintModal;
